'use client';
import { FC, useEffect, useState } from 'react';
import Head from 'next/head';
import ArrowForwardIcon from '@mildang/icons/react/arrow-right';
import { css } from '@mildang/styled-system/css';
import { css as emotionCSS } from '@emotion/react';
import { Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mildang/design-system-legacy/esm/components/molecules/Container';
import FooterInfo from '@mildang/design-system-legacy/esm/components/molecules/FooterInfo';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SignupHeader } from 'components/Header';
import LoginForm from './LoginForm';
import StudyLoginScreen from './StudyLoginScreen';
import { gql, useQuery } from '@apollo/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { sanitizeReturnUrl } from '../../sanitize-return-url';
import { isDomainAuthProject, isProject, isRenewalProject, select } from '@shared/constants/projectTarget';

const rootContainerStyle = css({
  overflowY: 'auto',
  height: '100%',
});

const rootStyle = ({ theme, isNew }: { theme: Theme; isNew: boolean }) => emotionCSS`
  background-color: ${theme.palette.background.secondary};
  ${
    isNew
      ? emotionCSS`
        background-color: #fff9ef;
      `
      : ''
  }
  min-block-size: var(--fullHeight, 100vh);
  inline-size: 100%;
  display: grid;
`;

const containerStyle = (theme: Theme) => emotionCSS`
  block-size: 100%;
  /* FIXME: CSS inject order 순서떄문에 임시로 !important*/
  display: grid !important;
  grid-template-columns: minmax(auto, 461px) 416px;
  align-items: center;
  justify-content: space-between;

  ${theme.breakpoints.down('lg')} {
    grid-template-columns: auto 400px;
    padding-left: 72px;
    padding-right: 72px;
  }
  ${theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    align-items: flex-start;
    grid-row-gap: 48px;
    justify-items: center;
    padding-left: 24px;
    padding-right: 24px;
  }
  ${theme.breakpoints.down('sm')} {
    grid-template-rows: auto 1fr;
    grid-row-gap: 24px;
    padding-left: 0px;
    padding-right: 0px;
    max-width: 100%;
  }
`;

const containerCenterStyle = (theme: Theme) => emotionCSS`
  block-size: 100%;
  display: grid !important;
  grid-template-columns: 416px;
  align-items: center;
  justify-content: center;
  justify-items: center;

  ${theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
    padding-left: 0px;
    padding-right: 0px;
    max-width: 100%;
  }
`;

const descStyle = (theme: Theme) => emotionCSS`
  z-index: 1;
  ${theme.breakpoints.down('md')} {
    text-align: center;
    align-self: flex-end;
  }
  ${theme.breakpoints.down('sm')} {
    padding-top: 32px;
  }
`;

// TODO: use clamp
const fluidFontSize = ({
  minFontSize,
  maxFontSize,
  minScreen,
  maxScreen,
}: {
  minFontSize: number;
  maxFontSize: number;
  minScreen: number;
  maxScreen: number;
}) => {
  return `calc(${minFontSize}px + (${maxFontSize} - ${minFontSize}) * (100vw - ${minScreen}px) / (${maxScreen} - ${minScreen}))`;
};

const titleStyle = (theme: Theme) => emotionCSS`
  white-space: pre-line;
  font-weight: 300;

  font-size: ${theme.typography.pxToRem(48)};

  ${theme.breakpoints.down('lg')} {
    font-size: ${fluidFontSize({
      maxScreen: 1279.9,
      minScreen: 960,
      minFontSize: 38,
      maxFontSize: 48,
    })};
  }
  ${theme.breakpoints.down('md')} {
    font-size: ${theme.typography.pxToRem(38)};
  }
  ${theme.breakpoints.down('sm')} {
    font-size: ${theme.typography.pxToRem(28)};
  }
`;
const emphasizeStyle = (theme: Theme) => emotionCSS`
  color: ${theme.palette.secondary.main};
`;
const secondaryStyle = (theme: Theme) => emotionCSS`
  margin-top: 24px;
  white-space: pre-line;
  font-size: ${theme.typography.pxToRem(18)};

  ${theme.breakpoints.down('lg')} {
    font-size: ${fluidFontSize({
      maxScreen: 1279.9,
      minScreen: 960,
      minFontSize: 16,
      maxFontSize: 18,
    })};
    margin-top: ${fluidFontSize({
      maxScreen: 1279.9,
      minScreen: 960,
      minFontSize: 16,
      maxFontSize: 24,
    })};
  }
  ${theme.breakpoints.down('md')} {
    margin-top: 16px;
    font-size: ${theme.typography.pxToRem(16)};
  }
  ${theme.breakpoints.down('sm')} {
    margin-top: 12px;
    font-size: ${theme.typography.pxToRem(14)};
  }
`;
const loginFormStyle = (theme: Theme) => emotionCSS`
  ${theme.breakpoints.down('md')} {
    max-width: 400px;
    width: 100%;
  }
  ${theme.breakpoints.down('sm')} {
    max-width: 100%;
    height: 100%;
  }
`;
const detailButtonStyle = (theme: Theme) => emotionCSS`
  margin-top: 16px;
  margin-left: -12px;
  ${theme.breakpoints.down('lg')} {
    margin-top: ${fluidFontSize({
      maxScreen: 1279.9,
      minScreen: 960,
      minFontSize: 8,
      maxFontSize: 16,
    })};
  }
  ${theme.breakpoints.down('md')} {
    margin-top: 8px;
  }
`;

interface ILoginScreenProps {}

const LoginScreen: FC<ILoginScreenProps> = () => {
  const matches = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  const [isNew, setIsNew] = useState(true);

  const { data } = useQuery(
    gql`
      query AuthorizeRequire {
        me {
          id
          name
        }
      }
    `,
  );

  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (data?.me?.id) {
      const returnUrl = sanitizeReturnUrl(searchParams?.get('returnUrl'));
      router.push(returnUrl);
    }
  }, [data?.me?.id, router, searchParams]);

  useEffect(() => {
    const pos = window.location.hostname.indexOf('study');
    if (pos >= 0) setIsNew(true);
  }, []);

  // const { data, loading } = useLoginScreenQuery({
  //   // fetchPolicy: 'network-only',
  // });
  // const { push } = useRouter();

  // const viewerId = data?.viewer?.id;
  // useEffect(() => {
  //   if (!loading && viewerId) {
  //     push('/').catch(() => {});
  //   }
  // }, [viewerId, loading, push]);

  const isDomainAuthTarget = isDomainAuthProject();
  const isMildang = isProject('mildang');
  const isAfterSchool = isProject('afterSchool');

  // 리뉴얼 화면은 밀당과 공교육(서울런·스쿨PT·라스쿨PT)이 함께 쓰고 로고·패널 색만 다르다.
  if (isRenewalProject()) return <StudyLoginScreen />;

  return (
    <div className={rootContainerStyle}>
      <div css={(theme) => rootStyle({ theme, isNew })}>
        <Head>
          <meta property="log:screenName" content="Login" />
        </Head>
        <SignupHeader />
        <Container css={isAfterSchool ? containerCenterStyle : containerStyle} fullFluid>
          {!isAfterSchool && (
            <div css={descStyle}>
              <Typography variant="h1" css={titleStyle}>
                <b>
                  {select({
                    slearn: '서울런',
                    laschool: '라스쿨PT',
                    _default: (
                      <>
                        1:1 <span css={emphasizeStyle}>온택트</span>과외
                      </>
                    ),
                  })}
                </b>
                {!isMildang ? '으' : ''}로{'\n'}
                <b>내신 & 수능</b>
                {' 대비 시작!'}
              </Typography>
              {isMildang && (
                <>
                  <Typography variant="body1" css={secondaryStyle} color="text.secondary">
                    {'일반 학원은 따라올 수 없는 성적 향상을 위한\n맞춤 수업과 관리를 밀당PT에서 경험해보세요!'}
                  </Typography>
                  <Button
                    data-ga-on="click"
                    data-ga-name="lookMore_btn"
                    onClick={() => {
                      const ISANDROID = () => {
                        // @ts-ignore
                        const ua = window?.navigator?.userAgent as string;
                        if (!ua) {
                          return false;
                        }
                        return /Android/i.test(ua) ? true : false;
                      };
                      if (ISANDROID()) {
                        location.href = 'https://mildang.kr';
                      } else {
                        window.open(
                          'https://mildang.kr',
                          '_blank', // <- This is what makes it open in a new window.
                        );
                      }
                    }}
                    css={detailButtonStyle}
                    color="secondary"
                    variant="text"
                    size={matches ? 'large' : 'xLarge'}
                    endIcon={<ArrowForwardIcon />}
                  >
                    자세히 알아보기
                  </Button>
                </>
              )}
            </div>
          )}
          <div css={loginFormStyle}>
            <LoginForm isNewWebsite={isNew} />
          </div>
        </Container>
      </div>
      {!isDomainAuthTarget && (
        <FooterInfo
          termsHref={isAfterSchool ? '/terms/20260129' : undefined}
          privacyHref={isAfterSchool ? '/privacy/schoolpt' : undefined}
        />
      )}
    </div>
  );
};

export default LoginScreen;
